import React from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Alert,
  Empty,
  Divider,
  Table,
  Tag,
  Space,
  Progress,
  Button,
  Spin,
} from 'antd';
import {
  DollarOutlined,
  MedicineBoxOutlined,
  TeamOutlined,
  CarOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  AggregatedCycleData,
  renderTotalCol,
  getTagRecord,
  formatDateTime,
} from './cycleDetailHelpers';

export interface CycleDetailInputsTabProps {
  aggregatedData: AggregatedCycleData;
  isLoadingLogs: boolean;
  isMobile?: boolean;
  filterBar: React.ReactNode;
  onSelectLog: (log: any) => void;
}

export const CycleDetailInputsTab: React.FC<CycleDetailInputsTabProps> = ({
  aggregatedData,
  isLoadingLogs,
  isMobile = false,
  filterBar,
  onSelectLog,
}) => {
  const {
    totalOperationalCost,
    totalMaterialCost,
    totalLaborCost,
    totalExpenseCost,
    totalPurchasedCost,
    totalPurchasedBase,
    totalPurchasedTaxes,
    purchasedInputs,
    appliedMaterialInputs,
    appliedLaborInputs,
    appliedExpenseInputs,
    allInputsRaw,
    appliedSummaryData,
    appliedInputs,
    purchasesSummaryData,
  } = aggregatedData;

  return (
    <Card bodyStyle={{ padding: isMobile ? '12px 8px' : '20px' }}>
      {filterBar}
      {isLoadingLogs ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* KPIs de Costos Discriminados */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12} md={8} xl={5} style={{ flex: '1 1 200px' }}>
              <Card
                size="small"
                style={{
                  background: '#f0f5ff',
                  borderColor: '#adc6ff',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <Tooltip title="Costo operativo acumulado real del ciclo: insumos físicos aplicados + jornales/labores + servicios externos consumidos">
                      <span style={{ color: '#1d39c4', fontWeight: 600 }}>
                        Costo Operativo Total ℹ️
                      </span>
                    </Tooltip>
                  }
                  value={totalOperationalCost}
                  prefix={<DollarOutlined style={{ color: '#1d39c4' }} />}
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  valueStyle={{ color: '#1d39c4', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} xl={5} style={{ flex: '1 1 200px' }}>
              <Card
                size="small"
                style={{
                  background: '#f6ffed',
                  borderColor: '#b7eb8f',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <span style={{ color: '#389e0d', fontWeight: 600 }}>
                      Insumos Físicos
                    </span>
                  }
                  value={totalMaterialCost}
                  prefix={<MedicineBoxOutlined style={{ color: '#389e0d' }} />}
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  suffix={
                    <span style={{ fontSize: 12, color: '#888' }}>
                      ({appliedMaterialInputs.length} apl.)
                    </span>
                  }
                  valueStyle={{ color: '#389e0d', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} xl={5} style={{ flex: '1 1 200px' }}>
              <Card
                size="small"
                style={{
                  background: '#fff0f6',
                  borderColor: '#ffadd2',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <span style={{ color: '#c41d7f', fontWeight: 600 }}>
                      Mano de Obra / Jornales
                    </span>
                  }
                  value={totalLaborCost}
                  prefix={<TeamOutlined style={{ color: '#c41d7f' }} />}
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  suffix={
                    <span style={{ fontSize: 12, color: '#888' }}>
                      ({appliedLaborInputs.length} jorn.)
                    </span>
                  }
                  valueStyle={{ color: '#c41d7f', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} xl={5} style={{ flex: '1 1 200px' }}>
              <Card
                size="small"
                style={{
                  background: '#e6f7ff',
                  borderColor: '#91d5ff',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <span style={{ color: '#096dd9', fontWeight: 600 }}>
                      Servicios y Gastos
                    </span>
                  }
                  value={totalExpenseCost}
                  prefix={<CarOutlined style={{ color: '#096dd9' }} />}
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  suffix={
                    <span style={{ fontSize: 12, color: '#888' }}>
                      ({appliedExpenseInputs.length} serv.)
                    </span>
                  }
                  valueStyle={{ color: '#096dd9', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} xl={5} style={{ flex: '1 1 200px' }}>
              <Card
                size="small"
                style={{
                  background: '#fffaf6',
                  borderColor: '#ffd591',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <Tooltip
                      title={`Inversión total en compras facturadas para inventario/almacén.${
                        totalPurchasedTaxes > 0
                          ? ` (Base: $ ${new Intl.NumberFormat('es-CO').format(
                              totalPurchasedBase
                            )} | IVA: $ ${new Intl.NumberFormat('es-CO').format(
                              totalPurchasedTaxes
                            )})`
                          : ''
                      }`}
                    >
                      <span style={{ color: '#fa8c16', fontWeight: 600 }}>
                        Compras Realizadas ℹ️
                      </span>
                    </Tooltip>
                  }
                  value={totalPurchasedCost}
                  prefix={<ShoppingCartOutlined style={{ color: '#fa8c16' }} />}
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  suffix={
                    <span style={{ fontSize: 12, color: '#888' }}>
                      ({purchasedInputs.length} comp.
                      {totalPurchasedTaxes > 0
                        ? ` | IVA: $${new Intl.NumberFormat('es-CO').format(
                            totalPurchasedTaxes
                          )}`
                        : ''}
                      )
                    </span>
                  }
                  valueStyle={{ color: '#d46b08', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          </Row>

          {purchasedInputs.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 6, fontSize: 13 }}
              message="Discriminación de Costos del Ciclo"
              description="El costo operativo incluye únicamente los insumos físicos aplicados, las labores/jornales y los servicios/gastos consumidos directamente. Las compras de insumos aumentan el inventario y se contabilizan por separado para evitar duplicación de costos."
            />
          )}

          {allInputsRaw.length === 0 ? (
            <Empty description="No se registraron insumos ni servicios aplicados o comprados en el periodo seleccionado." />
          ) : (
            <>
              {/* Resumen por Insumo/Servicio Aplicado */}
              {appliedSummaryData.length > 0 && (
                <>
                  <Divider
                    orientation="left"
                    style={{ fontWeight: 600, color: '#1d39c4' }}
                  >
                    📊 Insumos y Labores Aplicados (Consumo en Proceso)
                  </Divider>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={appliedSummaryData}
                    rowKey="id"
                    scroll={{ x: 700 }}
                    style={{ marginBottom: 28 }}
                    columns={[
                      {
                        title: 'Insumo / Servicio / Labor',
                        dataIndex: 'name',
                        key: 'name',
                        width: 220,
                        render: (val, record: any) => {
                          const logType = record.log_type;
                          const isLabor = logType === 'LABOR_LOG';
                          const isExpense = logType === 'SERVICE_EXPENSE';
                          const isServ =
                            record.is_service ||
                            record.register_type === 'SERVICE' ||
                            isLabor ||
                            isExpense;
                          let tagLabel = 'Insumo';
                          let tagColor = 'green';
                          if (isLabor) {
                            tagLabel = 'Jornal';
                            tagColor = 'magenta';
                          } else if (isExpense) {
                            tagLabel = 'Servicio/Gasto';
                            tagColor = 'geekblue';
                          } else if (isServ) {
                            tagLabel = 'Servicio';
                            tagColor = 'geekblue';
                          }
                          return (
                            <Space>
                              <Tag
                                color={tagColor}
                                style={{ fontSize: 10, margin: 0 }}
                              >
                                {tagLabel}
                              </Tag>
                              <strong>{val}</strong>
                            </Space>
                          );
                        },
                      },
                      {
                        title: 'Cant. Total Aplicada',
                        dataIndex: 'totalQuantity',
                        key: 'totalQuantity',
                        width: 130,
                        align: 'center',
                        render: (val, record: any) => (
                          <Tag color="blue">
                            {val} {record.measure_unit || ''}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Veces Aplicado',
                        dataIndex: 'count',
                        key: 'count',
                        width: 110,
                        align: 'center',
                        render: (val) => `${val} vez${val > 1 ? 'es' : ''}`,
                      },
                      {
                        title: 'Costo Total',
                        dataIndex: 'totalCost',
                        key: 'totalCost',
                        width: 140,
                        render: (val) => (
                          <span
                            style={{ fontWeight: 600, color: '#1890ff' }}
                          >
                            $ {new Intl.NumberFormat('es-CO').format(val)}
                          </span>
                        ),
                      },
                      {
                        title: '% del Costo Op.',
                        key: 'pct',
                        width: 140,
                        render: (_, record) => {
                          const pct =
                            totalOperationalCost > 0
                              ? (record.totalCost / totalOperationalCost) * 100
                              : 0;
                          return (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 100,
                              }}
                            >
                              <Progress
                                percent={Math.round(pct)}
                                size="small"
                                strokeColor="#1890ff"
                                style={{ flex: 1 }}
                              />
                            </div>
                          );
                        },
                      },
                    ]}
                    summary={() => (
                      <Table.Summary.Row
                        style={{ background: '#fafafa', fontWeight: 'bold' }}
                      >
                        <Table.Summary.Cell index={0}>
                          Total Costo Operativo
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="center">
                          <Tag color="geekblue">
                            {appliedSummaryData.reduce(
                              (s, i) => s + i.totalQuantity,
                              0
                            )}
                          </Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="center">
                          {appliedInputs.length} aplicaciones
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <span style={{ color: '#1890ff' }}>
                            $ {new Intl.NumberFormat('es-CO').format(
                              totalOperationalCost
                            )}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>100%</Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </>
              )}

              {/* Sección de Compras de Insumos Registradas */}
              {purchasedInputs.length > 0 && (
                <>
                  <Divider
                    orientation="left"
                    style={{ fontWeight: 600, color: '#d46b08' }}
                  >
                    🛒 Compras de Insumos Realizadas (Ingreso a Inventario)
                  </Divider>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={purchasesSummaryData}
                    rowKey="id"
                    scroll={{ x: 700 }}
                    style={{ marginBottom: 28 }}
                    columns={[
                      {
                        title: 'Insumo Comprado',
                        dataIndex: 'name',
                        key: 'name',
                        width: 220,
                        render: (val) => (
                          <Space>
                            <Tag
                              color="orange"
                              style={{ fontSize: 10, margin: 0 }}
                            >
                              Compra
                            </Tag>
                            <strong>{val}</strong>
                          </Space>
                        ),
                      },
                      {
                        title: 'Cant. Total Comprada',
                        dataIndex: 'totalQuantity',
                        key: 'totalQuantity',
                        width: 130,
                        align: 'center',
                        render: (val, record: any) => (
                          <Tag color="orange">
                            {val} {record.measure_unit || ''}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Veces Comprado',
                        dataIndex: 'count',
                        key: 'count',
                        width: 110,
                        align: 'center',
                        render: (val) =>
                          `${val} compra${val > 1 ? 's' : ''}`,
                      },
                      {
                        title: 'Total Facturado',
                        key: 'totalWithTaxes',
                        width: 150,
                        render: (_, record: any) => (
                          <div>
                            <span
                              style={{
                                fontWeight: 600,
                                color: '#fa8c16',
                              }}
                            >
                              ${' '}
                              {new Intl.NumberFormat('es-CO').format(
                                record.totalWithTaxes
                              )}
                            </span>
                            {record.totalTaxes > 0 && (
                              <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                Base: $
                                {new Intl.NumberFormat('es-CO').format(
                                  record.totalCost
                                )}{' '}
                                | IVA: $
                                {new Intl.NumberFormat('es-CO').format(
                                  record.totalTaxes
                                )}
                              </div>
                            )}
                          </div>
                        ),
                      },
                      {
                        title: '% de Compras',
                        key: 'pct',
                        width: 140,
                        render: (_, record: any) => {
                          const pct =
                            totalPurchasedCost > 0
                              ? (record.totalWithTaxes / totalPurchasedCost) *
                                100
                              : 0;
                          return (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 100,
                              }}
                            >
                              <Progress
                                percent={Math.round(pct)}
                                size="small"
                                strokeColor="#fa8c16"
                                style={{ flex: 1 }}
                              />
                            </div>
                          );
                        },
                      },
                    ]}
                    summary={() => (
                      <Table.Summary.Row
                        style={{
                          background: '#fffaf6',
                          fontWeight: 'bold',
                        }}
                      >
                        <Table.Summary.Cell index={0}>
                          Total Inversión en Compras
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="center">
                          <Tag color="orange">
                            {purchasesSummaryData.reduce(
                              (s, i) => s + i.totalQuantity,
                              0
                            )}
                          </Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="center">
                          {purchasedInputs.length} compras
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <div>
                            <span style={{ color: '#d46b08' }}>
                              ${' '}
                              {new Intl.NumberFormat('es-CO').format(
                                totalPurchasedCost
                              )}
                            </span>
                            {totalPurchasedTaxes > 0 && (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: '#8c8c8c',
                                  fontWeight: 'normal',
                                }}
                              >
                                Base: $
                                {new Intl.NumberFormat('es-CO').format(
                                  totalPurchasedBase
                                )}{' '}
                                | IVA: $
                                {new Intl.NumberFormat('es-CO').format(
                                  totalPurchasedTaxes
                                )}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>100%</Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </>
              )}

              {/* Detalle Cronológico de Insumos y Compras */}
              <Divider
                orientation="left"
                style={{ fontWeight: 600, color: '#262626' }}
              >
                📋 Detalle Cronológico de Insumos, Servicios y Compras
              </Divider>
              <Table
                size="small"
                dataSource={allInputsRaw}
                rowKey={(record) => `${record.id}-${record.log_date}`}
                scroll={{ x: 920 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                }}
                columns={[
                  {
                    title: 'Fecha',
                    dataIndex: 'log_date',
                    key: 'log_date',
                    render: (val) => formatDateTime(val),
                    width: 150,
                  },
                  {
                    title: 'Tipo Evento',
                    key: 'event_type',
                    width: 120,
                    align: 'center',
                    render: (_, record: any) => {
                      const lType = record.parent_log?.log_type;
                      if (lType === 'SUPPLY_PURCHASE') {
                        return (
                          <Tag
                            color="orange"
                            style={{ fontSize: 11, margin: 0 }}
                          >
                            🛒 Compra
                          </Tag>
                        );
                      }
                      if (lType === 'LABOR_LOG') {
                        return (
                          <Tag
                            color="magenta"
                            style={{ fontSize: 11, margin: 0 }}
                          >
                            🧑‍🌾 Jornal
                          </Tag>
                        );
                      }
                      if (lType === 'SERVICE_EXPENSE') {
                        return (
                          <Tag
                            color="geekblue"
                            style={{ fontSize: 11, margin: 0 }}
                          >
                            🚚 Servicio
                          </Tag>
                        );
                      }
                      return (
                        <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                          🌾 Insumo
                        </Tag>
                      );
                    },
                  },
                  {
                    title: 'Etapa',
                    dataIndex: 'stage_name',
                    key: 'stage',
                    width: 100,
                    render: (val) => (val ? <Tag>{val}</Tag> : '-'),
                  },
                  {
                    title: 'Insumo / Servicio',
                    key: 'element_name',
                    width: 170,
                    render: (_, record) => {
                      const isServ =
                        record.resolved_is_service ||
                        record.storage_elements?.register_type === 'SERVICE';
                      return (
                        <div>
                          <b>
                            {record.resolved_name ||
                              record.storage_elements?.name ||
                              'Insumo'}
                          </b>
                          {isServ && (
                            <Tag
                              color="geekblue"
                              style={{ fontSize: 10, marginLeft: 4 }}
                            >
                              Servicio
                            </Tag>
                          )}
                          {record.resolved_code && (
                            <span
                              style={{
                                fontSize: 10,
                                color: '#8c8c8c',
                                marginLeft: 4,
                              }}
                            >
                              ({record.resolved_code})
                            </span>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Cant.',
                    dataIndex: 'quantity',
                    key: 'qty',
                    width: 80,
                    align: 'center',
                  },
                  {
                    title: 'Costo / Precio U.',
                    dataIndex: 'unit_price',
                    key: 'price',
                    width: 120,
                    render: (val, record: any) => {
                      if (!val) return '-';
                      return (
                        <div>
                          <span>
                            ${new Intl.NumberFormat('es-CO').format(val)}
                          </span>
                          {record.tax_amount > 0 && (
                            <div style={{ fontSize: 10, color: '#fa8c16' }}>
                              + IVA ($
                              {new Intl.NumberFormat('es-CO').format(
                                record.tax_amount
                              )}
                              )
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Total',
                    key: 'total',
                    width: 120,
                    render: renderTotalCol,
                  },
                  {
                    title: 'Responsable / Proveedor',
                    key: 'contact',
                    width: 130,
                    render: (record) => getTagRecord(record),
                  },
                  {
                    title: 'Detalle',
                    key: 'action',
                    width: 80,
                    align: 'center',
                    render: (_, record) => (
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        onClick={() => onSelectLog(record.parent_log)}
                      >
                        Ver Log
                      </Button>
                    ),
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
};
